if (typeof LargeCardCarousel === 'undefined') {
    class LargeCardCarousel extends HTMLElement {
        connectedCallback() {
            this.slideContentHeight = 0;
            this.handleResize = this.handleResize.bind(this);

            const swiperContainer = this.querySelector('.swiper');
            if (!swiperContainer || this.swiper) return;

            whenReady('Swiper', () => {
                try {
                    // Check if this is a continuous swiper
                    if (swiperContainer.classList.contains('swiper-continuous')) {
                        this.initializeContinuousCarousel(swiperContainer);
                    } else {
                        this.initializeStandardCarousel(swiperContainer);
                    }

                } catch (error) {
                    console.error('Failed to initialize Swiper:', error);
                }
            });
        }

        initializeStandardCarousel(swiperContainer) {
            this.swiper = new Swiper(swiperContainer, {
                loop: true,
                a11y: true,
                mousewheel: {
                    forceToAxis: true
                },
                slidesPerView: "auto",
                pauseOnMouseEnter: false,
                modules: [Swiper.Autoplay, Swiper.A11y, Swiper.Mousewheel],
            });

            this.updateSlideContentHeight(); // Initial setup
            window.addEventListener('resize', this.handleResize);
        }

        initializeContinuousCarousel(swiperContainer) {
            this.swiperContainer = swiperContainer;
            this.isMobile = window.innerWidth <= 767;
            
            if (this.isMobile) {
                // Use regular Swiper on mobile
                this.initializeStandardCarousel(swiperContainer);
            } else {
                // Use continuous animation on desktop
                whenReady('gsap', () => {
                    whenReady('Draggable', () => {
                        whenReady('InertiaPlugin', () => {
                            this.setupContinuousAnimation(swiperContainer);
                        });
                    });
                });
            }
            
            // Add resize handler to switch between modes
            this.handleContinuousResize = this.handleContinuousResize.bind(this);
            window.addEventListener('resize', this.handleContinuousResize);
        }

        setupContinuousAnimation(swiperContainer) {
            const swiperWrapper = swiperContainer.querySelector('.swiper-wrapper');
            if (!swiperWrapper) return;

            // Get scroll duration from CSS custom property
            const scrollDuration = parseInt(getComputedStyle(swiperWrapper).getPropertyValue('--scroll-duration')) || 100;
            
            // Store original content for reset
            this.originalWrapperContent = swiperWrapper.innerHTML;
            
            // Create multiple swiper-wrapper elements for seamless looping
            const wrapper2 = swiperWrapper.cloneNode(true);
            const wrapper3 = swiperWrapper.cloneNode(true);
            
            // Append the cloned wrappers to the container
            swiperContainer.appendChild(wrapper2);
            swiperContainer.appendChild(wrapper3);

            // Get dimensions
            const containerWidth = swiperContainer.offsetWidth;
            const singleWrapperWidth = swiperWrapper.offsetWidth;
            
            // Set initial position - animate the swiper-continuous container itself
            window.gsap.set(swiperContainer, { x: 0 });
            
            // Calculate animation duration - INVERTED: higher scroll-duration = faster scrolling (lower duration)
            // Base duration calculation, then invert so higher numbers = faster
            const baseDuration = (singleWrapperWidth * 3) / containerWidth; // 3 wrappers total
            const loopDuration = (baseDuration / (scrollDuration / 100)) * 2; // Invert: higher scroll-duration = faster, then double duration to make it slower
            
            // Create timeline
            const tl = window.gsap.timeline({ repeat: -1 });
            tl.to(swiperContainer, loopDuration, { 
                x: -singleWrapperWidth, 
                ease: "none"
            });
            
            // Create draggable instance
            let initialTimeScale = 1; // Store the original auto-scroll speed
            let currentTranslateX = 0; // Track the actual translate3d x value
            
            const draggable = new window.Draggable(swiperContainer, {
                type: "x",
                trigger: swiperContainer,
                inertia: true,
                onPressInit: () => {
                    // Capture the current timeScale before pausing
                    initialTimeScale = tl.timeScale();
                    tl.pause();
                },
                onDrag: function() {
                    // Update current translate value
                    currentTranslateX = this.x;
                    
                    const transformX = this.x % singleWrapperWidth;
                    let progressX = -transformX / singleWrapperWidth;
                    if (progressX < 0) {
                        progressX = 1 + (progressX % 1);
                    }
                    tl.progress(progressX);
                },
                onThrowUpdate: function() {
                    // Update current translate value
                    currentTranslateX = this.x;
                    
                    const transformX = this.x % singleWrapperWidth;
                    let progressX = -transformX / singleWrapperWidth;
                    if (progressX < 0) {
                        progressX = 1 + (progressX % 1);
                    }
                    tl.progress(progressX);
                },
                onThrowComplete: () => {
                    // Normalize currentTranslateX to stay within loop boundaries (0 to -singleWrapperWidth)
                    const normalizedTranslateX = currentTranslateX % singleWrapperWidth;
                    const clampedTranslateX = normalizedTranslateX > 0 ? normalizedTranslateX - singleWrapperWidth : normalizedTranslateX;
                    
                    // Calculate timeline progress to match the normalized position
                    const timelineProgress = Math.abs(clampedTranslateX) / singleWrapperWidth;
                    
                    // Calculate the exact time position in the timeline
                    const seekTime = timelineProgress * loopDuration;
                    
                    // Jump to the exact time position and play from there
                    tl.seek(seekTime);
                    
                    // Set the swiper container's translate to match the timeline
                    window.gsap.set(swiperContainer, { x: clampedTranslateX });
                    
                    // Play from the current position
                    tl.play();
                    
                    // Smoothly transition back to the original auto-scroll speed
                    window.gsap.fromTo(tl, 1.5, {timeScale: tl.timeScale()}, {timeScale: initialTimeScale, ease: "power2.out"});
                }
            });

            // Add mousewheel support
            this.setupMousewheelSupport(swiperContainer, tl, singleWrapperWidth, loopDuration);

            // Store references for cleanup
            this.continuousTimeline = tl;
            this.continuousDraggable = draggable;
        }

        setupMousewheelSupport(swiperContainer, timeline, singleWrapperWidth, loopDuration) {
            let isMousewheelActive = false;
            let mousewheelTimeout;
            let initialTimeScale = 1;
            let currentTranslateX = 0;
            let wheelVelocity = 0;
            let lastWheelTime = 0;
            let inertiaTween = null;
            
            const handleMousewheel = (e) => {
                const now = Date.now();
                const deltaTime = now - lastWheelTime;
                lastWheelTime = now;
                
                // Use horizontal wheel delta (deltaX) for horizontal scrolling
                const deltaX = e.deltaX || 0; // Some mice/browsers use deltaX for horizontal
                const deltaY = e.deltaY || 0;
                
                // Only handle genuine horizontal scroll events, let vertical scroll pass through
                // Use a threshold to filter out small horizontal deltas from vertical scrolling
                const horizontalThreshold = 5; // Minimum deltaX to consider it a horizontal scroll
                const isVerticalScroll = Math.abs(deltaY) > Math.abs(deltaX);
                const isGenuineHorizontalScroll = Math.abs(deltaX) >= horizontalThreshold && !isVerticalScroll;
                
                if (!isGenuineHorizontalScroll) {
                    return; // Allow normal vertical scrolling or ignore small horizontal noise
                }
                
                // Prevent default only for horizontal scroll events
                e.preventDefault();
                
                // Use horizontal wheel delta for carousel control
                const horizontalDelta = deltaX;
                
                const wheelSpeed = 1.5; // Increased base speed multiplier
                const normalizedDelta = Math.min(Math.abs(horizontalDelta), 100) / 100; // Normalize wheel delta
                
                // Calculate velocity based on horizontal wheel delta and time (reversed direction)
                wheelVelocity = (horizontalDelta > 0 ? -1 : 1) * wheelSpeed * normalizedDelta;
                
                // Clear any existing timeout and inertia tween
                clearTimeout(mousewheelTimeout);
                if (inertiaTween) {
                    inertiaTween.kill();
                    inertiaTween = null;
                }
                
                // Capture current timeline state on first wheel event
                if (!isMousewheelActive) {
                    isMousewheelActive = true;
                    const currentProgress = timeline.progress();
                    currentTranslateX = -currentProgress * singleWrapperWidth;
                    initialTimeScale = timeline.timeScale();
                    timeline.pause();
                }
                
                // Update position based on wheel velocity (increased sensitivity)
                currentTranslateX += wheelVelocity * 40; // Increased multiplier for stronger movement
                
                // Keep within loop boundaries (similar to drag logic)
                const normalizedTranslateX = currentTranslateX % singleWrapperWidth;
                const clampedTranslateX = normalizedTranslateX > 0 ? normalizedTranslateX - singleWrapperWidth : normalizedTranslateX;
                
                // Calculate timeline progress to match the position
                const timelineProgress = Math.abs(clampedTranslateX) / singleWrapperWidth;
                
                // Update timeline and visual position
                timeline.progress(timelineProgress);
                window.gsap.set(swiperContainer, { x: clampedTranslateX });
                
                // Reset active state after wheel stops
                mousewheelTimeout = setTimeout(() => {
                    isMousewheelActive = false;
                    
                    // Normalize final position
                    const finalNormalizedX = currentTranslateX % singleWrapperWidth;
                    const finalClampedX = finalNormalizedX > 0 ? finalNormalizedX - singleWrapperWidth : finalNormalizedX;
                    
                    // Calculate final timeline progress
                    const finalProgress = Math.abs(finalClampedX) / singleWrapperWidth;
                    const seekTime = finalProgress * loopDuration;
                    
                    // Create inertia tween for smooth deceleration
                    const targetX = finalClampedX;
                    const currentX = window.gsap.getProperty(swiperContainer, "x");
                    const distance = targetX - currentX;
                    
                    // Create smooth inertia animation
                    inertiaTween = window.gsap.to(swiperContainer, {
                        x: targetX,
                        duration: Math.min(Math.abs(distance) / 100, 1.5), // Duration based on distance
                        ease: "power2.out",
                        onUpdate: function() {
                            const currentPos = window.gsap.getProperty(swiperContainer, "x");
                            const normalizedPos = currentPos % singleWrapperWidth;
                            const clampedPos = normalizedPos > 0 ? normalizedPos - singleWrapperWidth : normalizedPos;
                            const progress = Math.abs(clampedPos) / singleWrapperWidth;
                            timeline.progress(progress);
                        },
                        onComplete: function() {
                            // Jump to exact position and resume
                            timeline.seek(seekTime);
                            window.gsap.set(swiperContainer, { x: finalClampedX });
                            timeline.play();
                            
                            // Smoothly transition back to original auto-scroll speed
                            window.gsap.fromTo(timeline, 1.5, 
                                {timeScale: timeline.timeScale()}, 
                                {timeScale: initialTimeScale, ease: "power2.out"}
                            );
                            
                            inertiaTween = null;
                        }
                    });
                }, 150);
            };
            
            // Add mousewheel event listener
            swiperContainer.addEventListener('wheel', handleMousewheel, { passive: false });
            
            // Store reference for cleanup
            this.mousewheelHandler = handleMousewheel;
        }

        handleContinuousResize() {
            clearTimeout(this.continuousResizeTimeout);
            this.continuousResizeTimeout = setTimeout(() => {
                const wasMobile = this.isMobile;
                this.isMobile = window.innerWidth <= 767;
                
                // Only switch if the breakpoint has changed
                if (wasMobile !== this.isMobile) {
                    this.switchContinuousMode();
                }
            }, 150);
        }

        switchContinuousMode() {
            // Clean up current mode
            if (this.isMobile) {
                // Currently mobile, clean up continuous animation if it exists
                if (this.continuousTimeline) {
                    this.continuousTimeline.kill();
                    this.continuousTimeline = null;
                }
                if (this.continuousDraggable) {
                    this.continuousDraggable.kill();
                    this.continuousDraggable = null;
                }
                if (this.mousewheelHandler && this.swiperContainer) {
                    this.swiperContainer.removeEventListener('wheel', this.mousewheelHandler);
                    this.mousewheelHandler = null;
                }
                // Clean up any active inertia tween
                if (this.inertiaTween) {
                    this.inertiaTween.kill();
                    this.inertiaTween = null;
                }
                
                // Reset swiper-wrapper content if it was modified by continuous animation
                this.resetSwiperWrapper();
                
                // Initialize standard Swiper for mobile
                this.initializeStandardCarousel(this.swiperContainer);
            } else {
                // Currently desktop, clean up Swiper if it exists
                if (this.swiper) {
                    this.swiper.destroy(true, true);
                    this.swiper = null;
                }
                
                // Initialize continuous animation for desktop
                whenReady('gsap', () => {
                    whenReady('Draggable', () => {
                        whenReady('InertiaPlugin', () => {
                            this.setupContinuousAnimation(this.swiperContainer);
                        });
                    });
                });
            }
        }

        resetSwiperWrapper() {
            const swiperWrapper = this.swiperContainer.querySelector('.swiper-wrapper');
            if (!swiperWrapper) return;
            
            // Remove cloned wrappers (keep only the first one)
            const allWrappers = this.swiperContainer.querySelectorAll('.swiper-wrapper');
            for (let i = 1; i < allWrappers.length; i++) {
                allWrappers[i].remove();
            }
            
            // Restore original content if it was stored
            if (this.originalWrapperContent) {
                swiperWrapper.innerHTML = this.originalWrapperContent;
            }
            
            // Reset transform on container
            window.gsap.set(this.swiperContainer, { x: 0, clearProps: "all" });
            
            // Clear stored content
            this.originalWrapperContent = null;
        }

        disconnectedCallback() {
            if (this.swiper) {
                this.swiper.destroy(true, true);
                this.swiper = null;
            }
            
            // Clean up continuous carousel resources
            if (this.continuousTimeline) {
                this.continuousTimeline.kill();
                this.continuousTimeline = null;
            }
            if (this.continuousDraggable) {
                this.continuousDraggable.kill();
                this.continuousDraggable = null;
            }
            if (this.mousewheelHandler && this.swiperContainer) {
                this.swiperContainer.removeEventListener('wheel', this.mousewheelHandler);
                this.mousewheelHandler = null;
            }
            // Clean up any active inertia tween
            if (this.inertiaTween) {
                this.inertiaTween.kill();
                this.inertiaTween = null;
            }
            
            // Reset swiper wrapper if it was modified
            if (this.swiperContainer) {
                this.resetSwiperWrapper();
            }
            
            // Clean up resize handlers
            window.removeEventListener('resize', this.handleResize);
            if (this.handleContinuousResize) {
                window.removeEventListener('resize', this.handleContinuousResize);
            }
            
            // Clear any pending timeouts
            if (this.continuousResizeTimeout) {
                clearTimeout(this.continuousResizeTimeout);
            }
        }

        updateSlideContentHeight() {
            
            this.slideContentHeight = 0;
            const slides = this.querySelectorAll(".large-card__content");

            slides.forEach($slide => {
                $slide.style.height = 'auto'; // Reset before measuring
                if ($slide.clientHeight > this.slideContentHeight) {
                    this.slideContentHeight = $slide.clientHeight;
                }
            });

            slides.forEach($slide => {
                $slide.style.height = `${this.slideContentHeight}px`;
            });

        }

        handleResize() {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
                this.updateSlideContentHeight();
            }, 150);
        }
    }

    if (!window.customElements.get('large-card-carousel')) {
        window.customElements.define('large-card-carousel', LargeCardCarousel);
    }
}
